// ScanCapture — the in-store scanner surface. Mounts the
// BarcodeScanner camera, runs each detected UPC through the trip
// store's handleDetected action, and drives a single flash overlay +
// optional RedQuickNamePrompt. NO 4-state cascade per scan; viewState
// is one slice in the store, written once.
//
// Pair-anchoring: when armedListId is set, the next scan auto-pairs
// to that list slot AND inherits its canonical via the store's
// classifier. The user pre-arms by tapping the list row; the scan
// resolves immediately to green.

import { useEffect, useState } from "react";
import BarcodeScanner from "../BarcodeScanner";
import RedQuickNamePrompt from "./RedQuickNamePrompt";
import { useShopTrip, tripActions, selectScansArray } from "../../lib/useShopTrip";
import { TIER_COLORS } from "./tierColors";
import { COLOR, FONT, RADIUS, SPACE } from "../../lib/tokens";

const flashStripStyle = (tone) => ({
  position: "absolute",
  top: 0, left: 0, right: 0,
  height: 4,
  background: tone.strip,
  zIndex: 10,
  pointerEvents: "none",
});

const flashLabelStyle = (tone) => ({
  position: "absolute",
  top: SPACE.lg, left: "50%",
  transform: "translateX(-50%)",
  padding: "6px 12px",
  fontFamily: FONT.mono,
  fontSize: 11,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: COLOR.night,
  background: tone.strip,
  borderRadius: RADIUS.pill,
  zIndex: 11,
  whiteSpace: "nowrap",
  maxWidth: "80vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

// Scrim behind the red-name prompt — semi-opaque dark layer that
// dims the camera feed so the prompt reads clearly AND catches stray
// taps so they don't fall through to the BarcodeScanner. The
// scanner is paused via `paused={!!redPromptScanId}` already, but
// the scrim also gives the user a visual "you're answering this
// before scanning more" boundary.
const promptScrimStyle = {
  position: "absolute",
  inset: 0,
  background: "rgba(8, 6, 4, 0.55)",
  zIndex: 11,
  // Tap-through is blocked by default; clicking the scrim does
  // nothing (intentional — user must Save or Skip explicitly).
};

const promptOverlayStyle = {
  position: "absolute",
  bottom: SPACE.section,
  left: SPACE.section,
  right: SPACE.section,
  zIndex: 1000,
};

// ── Count prompt ("How many chops?") ────────────────────────────────
// Slim bottom strip for count-default scans that arrived without a
// package size. NON-BLOCKING by design: the camera keeps scanning,
// the strip never covers more than the bottom edge, and it auto-
// dismisses on TTL or on the next detection. Confirm writes the
// count into the scan's placement override (packageUnit: "count"),
// which is what flips the row yellow→green when brand is present.

const countStripStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  gap: SPACE.lg,
  padding: "10px 14px",
  background: COLOR.night,
  borderTop: `1px solid ${COLOR.border}`,
  zIndex: 12,
};

const countHeadlineStyle = {
  flex: 1,
  minWidth: 0,
  fontFamily: FONT.forma,
  fontStyle: "italic",
  fontWeight: 600,
  fontSize: 14,
  color: COLOR.ink,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const countStepBtnStyle = {
  width: 38,
  height: 38,
  flex: "0 0 auto",
  fontFamily: FONT.forma,
  fontSize: 18,
  lineHeight: 1,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  borderRadius: RADIUS.sm,
  cursor: "pointer",
  padding: 0,
};

const countValueStyle = {
  minWidth: 30,
  textAlign: "center",
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontSize: 18,
  color: COLOR.ink,
};

const countConfirmStyle = {
  flex: "0 0 auto",
  padding: "10px 14px",
  fontFamily: FONT.forma,
  fontWeight: 600,
  fontSize: 13,
  color: COLOR.night,
  background: COLOR.leaf,
  border: `1px solid ${COLOR.leaf}`,
  borderRadius: RADIUS.sm,
  cursor: "pointer",
};

const countSkipStyle = {
  flex: "0 0 auto",
  padding: "10px 8px",
  fontFamily: FONT.forma,
  fontSize: 13,
  color: COLOR.muted,
  background: "transparent",
  border: "none",
  cursor: "pointer",
};

export default function ScanCapture({ onCancel, shoppingList = [] }) {
  const flash = useShopTrip((s) => s.viewState.flash);
  const lookingUpc = useShopTrip((s) => s.viewState.lookingUpc);
  const countPrompt = useShopTrip((s) => s.viewState.countPrompt);
  const allScans = useShopTrip(selectScansArray);
  const [redPromptScanId, setRedPromptScanId] = useState(null);
  const [countDraft, setCountDraft] = useState(1);

  // Filter the shopping list to unpurchased + un-paired items, so the
  // "tap to place" chips in RedQuickNamePrompt only show slots that
  // are still available. Re-paired automatically by the existing
  // checkout-time pairing — but during the in-store flow the user
  // shouldn't be offered slots that already have a scan attached.
  const availableTargets = (shoppingList || []).filter((item) => {
    if (!item || item.purchasedAt) return false;
    const alreadyPaired = allScans.some((sc) => sc.pairedShoppingListItemId === item.id);
    return !alreadyPaired;
  });

  // Auto-clear the flash after its TTL. Single timer — no cascade.
  useEffect(() => {
    if (!flash) return;
    const remaining = Math.max(0, (flash.until || 0) - Date.now());
    const t = setTimeout(() => tripActions.clearFlash(), remaining);
    return () => clearTimeout(t);
  }, [flash]);

  // Count prompt — reset the stepper to the prompt's prefill whenever
  // a new prompt mounts (keyed on scanId so a re-render of the same
  // prompt doesn't stomp mid-tap edits).
  useEffect(() => {
    if (countPrompt) setCountDraft(countPrompt.prefill || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countPrompt?.scanId]);

  // Auto-dismiss the count prompt on its TTL — same single-timer
  // shape as the flash above. The store also clears it on the next
  // detection, so this only fires when the aisle goes quiet.
  useEffect(() => {
    if (!countPrompt) return;
    const remaining = Math.max(0, (countPrompt.until || 0) - Date.now());
    const t = setTimeout(() => tripActions.clearCountPrompt(), remaining);
    return () => clearTimeout(t);
  }, [countPrompt]);

  // The most-recent red scan that hasn't been named yet → mount the
  // RedQuickNamePrompt under the scanner. The store keeps the row, so
  // the user can dismiss the prompt and we still have the UPC for
  // the Resolve phase.
  const latestRed = allScans.length
    ? [...allScans].reverse().find((s) => s.status === "red" && !s.productName)
    : null;
  useEffect(() => {
    if (latestRed && latestRed.id !== redPromptScanId) {
      setRedPromptScanId(latestRed.id);
    } else if (!latestRed && redPromptScanId) {
      setRedPromptScanId(null);
    }
  }, [latestRed, redPromptScanId]);

  async function handleDetected(upc) {
    await tripActions.handleDetected(upc);
  }

  function handleSaveRed(scanId, name) {
    tripActions.setRedQuickName(scanId, name);
    setRedPromptScanId(null);
  }

  function handlePlaceRed(scanId, listItem) {
    tripActions.placeRedScan(scanId, listItem);
    setRedPromptScanId(null);
  }

  function handleSkipRed(scanId) {
    setRedPromptScanId(null);
  }

  function handleConfirmCount() {
    if (!countPrompt) return;
    const n = Math.max(1, Math.min(99, Math.round(countDraft) || 1));
    // Same write path as the ResolveCard size picker — the override
    // cascade reclassifies the scan and carries amount/max/unit
    // ("count") through commitTrip into the pantry row.
    tripActions.setPlacementOverride(countPrompt.scanId, {
      packageAmount: n,
      packageUnit: "count",
    });
    tripActions.clearCountPrompt();
  }

  const tone = flash ? TIER_COLORS[flash.tier] || TIER_COLORS.red : null;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <BarcodeScanner
        mode="rapid"
        embedded
        paused={!!redPromptScanId}
        onDetected={handleDetected}
        onCancel={onCancel}
      />
      {flash && tone && (
        <>
          <div style={flashStripStyle(tone)} />
          <div style={flashLabelStyle(tone)}>
            {tone.label} · {flash.name}
          </div>
        </>
      )}
      {lookingUpc && !flash && (
        <div style={flashLabelStyle(TIER_COLORS.yellow)}>
          LOOKING · {lookingUpc.slice(-6)}
        </div>
      )}
      {/* Count prompt — slim bottom strip, non-blocking. Hidden while
          the red-name prompt owns the bottom of the screen (the
          scanner is paused then anyway, and the prompt's TTL keeps
          state from lingering). */}
      {countPrompt && !redPromptScanId && (countPrompt.until || 0) > Date.now() && (
        <div style={countStripStyle}>
          <div style={countHeadlineStyle}>
            How many {countPrompt.label || "items"}?
          </div>
          <button
            type="button"
            style={countStepBtnStyle}
            onClick={() => setCountDraft((n) => Math.max(1, n - 1))}
            aria-label="One fewer"
          >
            −
          </button>
          <div style={countValueStyle}>{countDraft}</div>
          <button
            type="button"
            style={countStepBtnStyle}
            onClick={() => setCountDraft((n) => Math.min(99, n + 1))}
            aria-label="One more"
          >
            +
          </button>
          <button type="button" style={countConfirmStyle} onClick={handleConfirmCount}>
            Confirm
          </button>
          <button
            type="button"
            style={countSkipStyle}
            onClick={() => tripActions.clearCountPrompt()}
          >
            Skip
          </button>
        </div>
      )}
      {redPromptScanId && (
        <>
          <div style={promptScrimStyle} aria-hidden />
          <div style={promptOverlayStyle}>
            <RedQuickNamePrompt
              scanId={redPromptScanId}
              upc={allScans.find((s) => s.id === redPromptScanId)?.barcodeUpc}
              shoppingList={availableTargets}
              onPlace={handlePlaceRed}
              onSave={handleSaveRed}
              onSkip={handleSkipRed}
            />
          </div>
        </>
      )}
    </div>
  );
}
